// UI utilities for Docospot
// Handles dialogs, notifications, and DOM manipulation

/**
 * Show an element by ID
 * @param {string} elementId - Element ID
 */
export function showElement(elementId) {
    const element = document.getElementById(elementId);
    if (element) {
        element.style.display = 'block';
    }
}

/**
 * Hide an element by ID
 * @param {string} elementId - Element ID
 */
export function hideElement(elementId) {
    const element = document.getElementById(elementId);
    if (element) {
        element.style.display = 'none';
    }
}

/**
 * Show an error message
 * @param {string} message - Error message to display
 */
export function showError(message) {
    const errorDiv = document.getElementById('error-message');
    if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.style.display = 'block';
        setTimeout(() => {
            errorDiv.style.display = 'none';
        }, 5000);
    }
}

/**
 * Show a success message
 * @param {string} message - Success message to display
 * @param {number} duration - Duration in milliseconds (default 3000)
 */
export function showSuccess(message, duration = 3000) {
    const successDiv = document.getElementById('success-message');
    if (successDiv) {
        successDiv.textContent = message;
        successDiv.style.display = 'block';
        setTimeout(() => {
            successDiv.style.display = 'none';
        }, duration);
    }
}

/**
 * Show a warning message
 * @param {string} message - Warning message to display
 * @param {number} duration - Duration in milliseconds (default 8000)
 */
export function showWarning(message, duration = 8000) {
    // Try to find a warning div, otherwise create one or use error div with warning style
    let warningDiv = document.getElementById('warning-message');
    
    if (!warningDiv) {
        // Create a warning message div if it doesn't exist
        warningDiv = document.createElement('div');
        warningDiv.id = 'warning-message';
        warningDiv.className = 'fixed top-4 right-4 max-w-md p-4 rounded-lg shadow-lg z-50 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800';
        warningDiv.style.display = 'none';
        document.body.appendChild(warningDiv);
    }
    
    warningDiv.innerHTML = `
        <div class="flex">
            <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
            </div>
            <div class="ml-3">
                <p class="text-sm">${message}</p>
            </div>
        </div>
    `;
    warningDiv.style.display = 'block';
    
    setTimeout(() => {
        warningDiv.style.display = 'none';
    }, duration);
}

/**
 * Show a Tailwind UI confirmation dialog
 * @param {object} options - Dialog options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Dialog message
 * @param {string} options.confirmText - Confirm button text
 * @param {string} options.cancelText - Cancel button text
 * @param {string} options.confirmClass - Tailwind classes for confirm button
 * @param {string} options.icon - Icon type ('warning', 'danger', 'info')
 * @returns {Promise<boolean>} True if confirmed, false if cancelled
 */
export function showConfirmDialog(options) {
    const {
        title = 'Confirm Action',
        message = 'Are you sure you want to proceed?',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        confirmClass = 'bg-red-600 hover:bg-red-500',
        icon = 'warning'
    } = options;
    
    return new Promise((resolve) => {
        // Remove any existing confirm dialog
        const existing = document.getElementById('confirm-dialog');
        if (existing) existing.remove();
        
        const iconSvg = icon === 'danger' || icon === 'warning' ? `
            <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
        ` : `
            <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
        `;
        
        const modal = document.createElement('div');
        modal.id = 'confirm-dialog';
        modal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50';
        modal.style.opacity = '0';
        modal.innerHTML = `
            <div class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6" style="opacity: 0; transform: scale(0.95);">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                ${iconSvg}
                            </div>
                            <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                <h3 class="text-base font-semibold leading-6 text-gray-900">${title}</h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500">${message}</p>
                                </div>
                            </div>
                        </div>
                        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                            <button type="button" id="confirm-yes-btn" class="inline-flex w-full justify-center rounded-md ${confirmClass} px-3 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto transition-colors duration-150">${confirmText}</button>
                            <button type="button" id="confirm-no-btn" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-colors duration-150">${cancelText}</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        // Animate in
        requestAnimationFrame(() => {
            modal.style.transition = 'opacity 200ms ease-out';
            modal.style.opacity = '1';
            const content = modal.querySelector('.relative');
            content.style.transition = 'opacity 200ms ease-out, transform 200ms ease-out';
            content.style.opacity = '1';
            content.style.transform = 'scale(1)';
        });
        
        const cleanup = (result) => {
            // Animate out
            modal.style.opacity = '0';
            const content = modal.querySelector('.relative');
            content.style.opacity = '0';
            content.style.transform = 'scale(0.95)';
            
            setTimeout(() => {
                modal.remove();
                resolve(result);
            }, 200);
        };
        
        document.getElementById('confirm-yes-btn').onclick = () => cleanup(true);
        document.getElementById('confirm-no-btn').onclick = () => cleanup(false);
        
        // Close on backdrop click
        modal.onclick = (e) => {
            if (e.target === modal || e.target.classList.contains('fixed')) {
                cleanup(false);
            }
        };
        
        // Close on Escape key
        const escHandler = (e) => {
            if (e.key === 'Escape') {
                cleanup(false);
                document.removeEventListener('keydown', escHandler);
            }
        };
        document.addEventListener('keydown', escHandler);
    });
}

/**
 * Show a Tailwind UI alert dialog
 * @param {object} options - Dialog options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Dialog message
 * @param {string} options.confirmText - OK button text
 * @param {string} options.icon - Icon type ('success', 'error', 'warning', 'info')
 * @returns {Promise<void>}
 */
export function showAlertDialog(options) {
    const {
        title = 'Information',
        message = '',
        confirmText = 'OK',
        icon = 'info'
    } = options;
    
    return new Promise((resolve) => {
        // Remove any existing alert dialog
        const existing = document.getElementById('alert-dialog');
        if (existing) existing.remove();
        
        let iconSvg, iconBgColor, iconColor;
        
        if (icon === 'success') {
            iconBgColor = 'bg-green-100';
            iconColor = 'text-green-600';
            iconSvg = `
                <svg class="h-6 w-6 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            `;
        } else if (icon === 'error') {
            iconBgColor = 'bg-red-100';
            iconColor = 'text-red-600';
            iconSvg = `
                <svg class="h-6 w-6 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
            `;
        } else if (icon === 'warning') {
            iconBgColor = 'bg-yellow-100';
            iconColor = 'text-yellow-600';
            iconSvg = `
                <svg class="h-6 w-6 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
            `;
        } else {
            iconBgColor = 'bg-blue-100';
            iconColor = 'text-blue-600';
            iconSvg = `
                <svg class="h-6 w-6 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
            `;
        }
        
        const modal = document.createElement('div');
        modal.id = 'alert-dialog';
        modal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50';
        modal.style.opacity = '0';
        modal.innerHTML = `
            <div class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6" style="opacity: 0; transform: scale(0.95);">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${iconBgColor} sm:mx-0 sm:h-10 sm:w-10">
                                ${iconSvg}
                            </div>
                            <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                                <h3 class="text-base font-semibold leading-6 text-gray-900">${title}</h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 whitespace-pre-line">${message}</p>
                                </div>
                            </div>
                        </div>
                        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button type="button" id="alert-ok-btn" class="inline-flex w-full justify-center rounded-md bg-primary-600 hover:bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto transition-colors duration-150">${confirmText}</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        // Animate in
        requestAnimationFrame(() => {
            modal.style.transition = 'opacity 200ms ease-out';
            modal.style.opacity = '1';
            const content = modal.querySelector('.relative');
            content.style.transition = 'opacity 200ms ease-out, transform 200ms ease-out';
            content.style.opacity = '1';
            content.style.transform = 'scale(1)';
        });
        
        const cleanup = () => {
            // Animate out
            modal.style.opacity = '0';
            const content = modal.querySelector('.relative');
            content.style.opacity = '0';
            content.style.transform = 'scale(0.95)';
            
            setTimeout(() => {
                modal.remove();
                resolve();
            }, 200);
        };
        
        document.getElementById('alert-ok-btn').onclick = cleanup;
        
        // Close on backdrop click
        modal.onclick = (e) => {
            if (e.target === modal || e.target.classList.contains('fixed')) {
                cleanup();
            }
        };
        
        // Close on Escape key
        const escHandler = (e) => {
            if (e.key === 'Escape') {
                cleanup();
                document.removeEventListener('keydown', escHandler);
            }
        };
        document.addEventListener('keydown', escHandler);
    });
}

export default {
    showElement,
    hideElement,
    showError,
    showSuccess,
    showConfirmDialog,
    showAlertDialog
};
